let lastActiveTime: Date | null = null;

/**
 * Updates the last active timestamp to the current time.
 */
const updateLastActive = (): void => {
  lastActiveTime = new Date();

  onUserActivity();
};

/**
 * Returns the last active timestamp or null if no activity has been recorded.
 * @returns {Date | null} The Date object of the last activity.
 */
const getUserLastActive = (): Date | null => {
  return lastActiveTime;
};

type AnyFunction = (...args: any[]) => any;

let lastRun: Date | null = null;

function debounce<T extends AnyFunction>(
  func: T,
  wait: number = 1000
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  let lastArgs: Parameters<T> | null = null;

  const executeFunction = () => {
    if (lastArgs) {
      func(...lastArgs);
      lastArgs = null;
    }
  };

  return function debounced(...args: Parameters<T>) {
    lastArgs = args;

    // Clear the existing timeout
    if (timeout) {
      clearTimeout(timeout);
    }

    // Set new timeout
    timeout = setTimeout(() => {
      executeFunction();
      lastRun = new Date();
    }, wait);

    if (lastRun === null || +new Date() - +lastRun > wait) {
      executeFunction();
      lastRun = new Date();
    }
  };
}

const userBecomeActiveCallbacks = new Set<() => void>();
const userBecomeInactiveCallbacks = new Set<() => void>();

const USER_INACTIVE_THRESHOLD = 2 * 60 * 1000; // 2 minutes

const onUserBecomeActive = (callback: () => void): (() => void) => {
  userBecomeActiveCallbacks.add(callback);
  return () => userBecomeActiveCallbacks.delete(callback);
};

const onUserBecomeInactive = (callback: () => void): (() => void) => {
  userBecomeInactiveCallbacks.add(callback);
  return () => userBecomeInactiveCallbacks.delete(callback);
};

let inactivtyTimeout: ReturnType<typeof setTimeout> | null = null;

let isUserInactive = false;

const onUserActivity = () => {
  if (inactivtyTimeout) {
    clearTimeout(inactivtyTimeout);
  }
  inactivtyTimeout = setTimeout(() => {
    if (lastActiveTime && Date.now() - lastActiveTime.getTime() >= USER_INACTIVE_THRESHOLD) {
      isUserInactive = true;
      userBecomeInactiveCallbacks.forEach((callback) => callback());
    }
  }, USER_INACTIVE_THRESHOLD);

  if (isUserInactive) {
    isUserInactive = false;
    userBecomeActiveCallbacks.forEach((callback) => callback());
  }
};

const getUserIsActive = () => {
  return !isUserInactive;
};

/**
 * Initializes user activity tracking by attaching event listeners.
 */
const initializeUserActivityTracking = (): void => {
  const userActivityEvents: string[] = ['mousemove', 'keydown', 'click', 'scroll'];

  userActivityEvents.forEach((event) => {
    document.addEventListener(event, debounce(updateLastActive, 1000));
  });

  onUserActivity();
};

// Initialize activity tracking when the module is loaded
initializeUserActivityTracking();

// Export the getUserLastActive function for external use
export { getUserLastActive, getUserIsActive, onUserBecomeActive, onUserBecomeInactive };
